import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

/**
 * PageTransition - Wrapper component for smooth, modern page transitions
 * Uses Framer Motion with Apple-inspired animations
 * Respects user's reduced motion preferences
 *
 * Animation: Fade + subtle scale for depth (modern, premium feel)
 * Duration: 0.4s for smoothness without feeling sluggish
 * Easing: Custom cubic-bezier for natural, fluid motion
 */
export const PageTransition = ({ children }) => {
  const [isReady, setIsReady] = useState(false);

  // Let layout stabilize before starting animation
  useEffect(() => {
    // Use requestAnimationFrame to wait for next paint
    requestAnimationFrame(() => {
      setIsReady(true);
    });
  }, []);

  // Check if user prefers reduced motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Simplified animation for reduced motion
  if (prefersReducedMotion) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="flex-1 flex flex-col"
      >
        {children}
      </motion.div>
    );
  }

  // Full animation for normal motion preference
  return (
    <motion.div
      initial={false}
      animate={isReady ? {
        opacity: 1,
        scale: 1,
        y: 0
      } : {
        opacity: 0,
        scale: 0.98,
        y: 12
      }}
      exit={{
        opacity: 0,
        scale: 0.98,
        y: -12
      }}
      transition={{
        duration: 0.35,
        ease: [0.22, 1, 0.36, 1]
      }}
      className="flex-1 flex flex-col"
      style={{
        transformOrigin: 'center center',
        backfaceVisibility: 'hidden',
        WebkitFontSmoothing: 'antialiased'
      }}
    >
      {children}
    </motion.div>
  );
};
